import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import HudFrame from "@/components/primitives/HudFrame";

function timeAgo(iso) {
    try {
        const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
        if (s < 60) return "just now";
        if (s < 3600) return `${Math.round(s/60)}m ago`;
        if (s < 86400) return `${Math.round(s/3600)}h ago`;
        return `${Math.round(s/86400)}d ago`;
    } catch { return "—"; }
}

const STATUS_TINT = {
    active:    { dot: "bg-tca-cyan",     ring: "rgba(0,219,255,0.45)",  label: "text-tca-cyan-hi" },
    paused:    { dot: "bg-tca-gold",     ring: "rgba(201,168,106,0.4)", label: "text-tca-gold-warm" },
    completed: { dot: "bg-tca-cyan-hi",  ring: "rgba(110,247,255,0.5)", label: "text-tca-cyan-hi" },
    archived:  { dot: "bg-tca-ink-muted",ring: "rgba(255,255,255,0.15)",label: "text-tca-ink-muted" },
};

export default function ProjectCard({ project, index }) {
    const nav = useNavigate();
    const t = STATUS_TINT[project.status] || STATUS_TINT.active;
    const refs = project.doctrine_refs || [];
    const fcount = (project.file_ids || []).length;
    const mcount = (project.linked_modules || []).length;

    return (
        <motion.button
            type="button"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: project.status === "archived" ? 0.55 : 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.05 * index }}
            whileHover={{ y: -2 }}
            onClick={() => nav(`/portal/projects/${project.id}`)}
            data-testid={`project-card-${project.id}`}
            className="text-left group block"
        >
            <HudFrame
                bracketColor="var(--tca-cyan-core)"
                bracketSize={12}
                borderTint="rgba(0,219,255,0.18)"
                className="bg-tca-bg-panel/65 backdrop-blur-md hover:bg-tca-bg-panel/85 transition-all"
                innerClassName="p-5 min-h-[180px] flex flex-col"
            >
                <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                        <motion.span
                            animate={project.status === "active" ? { opacity: [0.5, 1, 0.5] } : { opacity: 0.5 }}
                            transition={{ duration: 2, repeat: project.status === "active" ? Infinity : 0 }}
                            className={`w-1.5 h-1.5 rounded-full ${t.dot}`}
                            style={{ boxShadow: `0 0 8px ${t.ring}` }}
                        />
                        <span className={`font-mono-tight text-[9px] uppercase tracking-[0.28em] ${t.label}`}>
                            {project.status}
                        </span>
                    </span>
                    <span className="font-mono-tight text-[9px] uppercase tracking-[0.22em] text-tca-ink-muted">
                        {timeAgo(project.last_active_at)}
                    </span>
                </div>

                <div className="mt-3 font-display text-xl text-tca-ink truncate group-hover:text-tca-cyan-hi transition-colors">
                    {project.name}
                </div>
                {project.description && (
                    <p className="mt-1 text-[12px] text-tca-ink-muted line-clamp-2">
                        {project.description}
                    </p>
                )}

                {/* Mini A-Z doctrine dots */}
                <div className="mt-4 flex flex-wrap gap-1" data-testid="project-card-doctrine-refs">
                    {refs.map((L) => (
                        <span
                            key={L}
                            className="inline-flex items-center justify-center w-5 h-5 font-mono-tight text-[9px] font-semibold border border-tca-cyan/60 text-tca-cyan-hi bg-tca-cyan/8"
                            style={{ boxShadow: "0 0 6px rgba(0,219,255,0.3)" }}
                        >
                            {L}
                        </span>
                    ))}
                    {refs.length === 0 && (
                        <span className="font-mono-tight text-[9px] uppercase tracking-[0.22em] text-tca-ink-muted/55">// no refs</span>
                    )}
                </div>

                <div className="mt-auto pt-4 flex justify-between font-mono-tight text-[9px] uppercase tracking-[0.22em] text-tca-ink-muted">
                    <span>{mcount} modules</span>
                    <span>{fcount} files</span>
                </div>
            </HudFrame>
        </motion.button>
    );
}
